import { Controller } from '@hotwired/stimulus';
import {
  hide as hideArticleActions,
  show as showArticleActions,
} from '@controllers/main/article-actions_controller';
import CurrentPage from '@frontend/app/features/CurrentPage';
import { dispatchCustomEvent } from '@frontend/utils/events';

export default class extends Controller {
  static targets = ['articleHeader', 'content', 'navbar'];
  static values = {
    navbarIsStickyToTop: { type: Boolean, default: false },
    previousScrollPosition: { type: Number, default: 0 },
    wasScrollingDown: { type: Boolean, default: false },
    navbarHidden: { type: Boolean, default: false },
    articleActionsHidden: { type: Boolean, default: true },
    forceNavbarDefaultColors: { type: Boolean, default: false },
  };

  connect() {
    if (!this.hasContentTarget || !this.hasNavbarTarget) return false;

    this.navbarPositionIsSticky = window.getComputedStyle(this.navbarTarget).position === "sticky";
    this.navbarHeight = this.navbarTarget.getBoundingClientRect().height;

    if (CurrentPage.isArticlePreview()) {
      this.setupArticlePreview();
    } else {
      this.setup();
    }
  }

  setupArticlePreview() {
    hideArticleActions();
    this.hideNavbar();
  }

  setup() {
    // NB: I tried to optimize this with the debounce function but as a result
    // the hide/show logic triggered too delayed on touch devices. I didn't take
    // the time to further investigate this.
    window.addEventListener('scroll', this.handleScrollChange.bind(this));
    document.body.addEventListener('article-scroll:article-actions-loaded', this.handleArticleActionsLoaded.bind(this));
    this.updateForceNavbarDefaultColorValue();
  }

  handleArticleActionsLoaded() {
    if (this.isScrollPositionInsideArticle()) {
      this.articleActionsHiddenValue = false;
    }
  }

  handleScrollChange() {
    if (this.isScrollPositionInsideArticle()) {
      const isScrollingDown = this.isScrollingDown();
      this.navbarHiddenValue = this.navbarPositionIsSticky && isScrollingDown;
      this.articleActionsHiddenValue = isScrollingDown;
    } else {
      this.navbarHiddenValue = false;
      this.articleActionsHiddenValue = true;
    }

    this.storePreviousScrollPosition();
    this.updateForceNavbarDefaultColorValue();
    dispatchCustomEvent("article-reading-progress:update");
  }

  navbarHiddenValueChanged() {
    if(this.navbarHiddenValue) {
      this.hideNavbar();
    } else {
      this.showNavbar();
    }
  }

  updateForceNavbarDefaultColorValue() {
    this.forceNavbarDefaultColorsValue = this.hasArticleHeaderTarget && this.articleHeaderOutOfView();
  }

  forceNavbarDefaultColorsValueChanged() {
    document.documentElement.classList.toggle('navbar-force-default-colors', this.forceNavbarDefaultColorsValue)
  }

  articleActionsHiddenValueChanged() {
    if(this.articleActionsHiddenValue) {
      hideArticleActions();
    } else {
      showArticleActions();
    }
  }

  storePreviousScrollPosition() {
    this.previousScrollPositionValue = this.currentScrollPosition();
  }

  currentScrollPosition() {
    return Math.abs(document.body.getBoundingClientRect().top);
  }

  articleHeaderOutOfView() {
    const details = this.articleHeaderTarget.getBoundingClientRect();
    return (Math.abs(details.top) + this.navbarHeight) > details.height;
  }

  isScrollPositionInsideArticle() {
    const contentPosFromTop = this.contentTarget.getBoundingClientRect().top;
    const contentHeight = this.contentTarget.clientHeight;
    const halfWindowHeight = window.innerHeight / 2;
    const contentScrolledHalfIntoWindow = contentPosFromTop < halfWindowHeight;
    const contentScrolledNotYetOutOfView = contentPosFromTop - halfWindowHeight + contentHeight > 0;
    const contentInView = contentScrolledHalfIntoWindow && contentScrolledNotYetOutOfView;

    return contentInView;
  }

  isScrollingDown() {
    return this.previousScrollPositionValue < this.currentScrollPosition();
  }

  hideNavbar() {
    if(this.hasNavbarTarget) {
      this.navbarTarget.classList.add('navbar-hidden');
    }
  }

  showNavbar() {
    if(this.hasNavbarTarget) {
      this.navbarTarget.classList.remove('navbar-hidden');
    }
  }
}
